import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Upload from '@mui/icons-material/Upload';
import { yupResolver } from '@hookform/resolvers/yup';
import { createStyles, makeStyles } from '@mui/styles';
import moment from 'moment-timezone';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { organizationApi } from 'src/api/organizationApi';
import { Button } from 'src/common';
import { RHFCustomSelect, RHFProvider, RHFTextField } from 'src/common/rhf';
import useAuth from 'src/hooks/useAuth';
import countriesData from 'src/pages/companies/add-company/countries.json';

const timeZones = moment.tz.names();
const dummyAvatar = 'https://assets.it22.nl/fortes-energy/admin/dummy_image.png';

const countryOptions = countriesData.countries.map((country) => ({
  id: country.name,
  label: country.name,
  value: country.name,
}));

const getCitiesByCountry = (countryName: string): string[] => {
  const country = countriesData.countries.find((item) => item.name === countryName);
  return country?.cities ?? [];
};

/**
 * Searchable/typeable autocomplete that keeps the RHF value as a plain string.
 * Local to this file to avoid changing the shared `RHFAutocompleteSync` contract
 * (that wrapper stores the full option object).
 */
const StringAutocomplete: FC<{
  name: Extract<keyof OrgSettingsFormValues, string>;
  outerLabel: string;
  placeholder: string;
  options: string[];
  disabled?: boolean;
  onChangeCallback?: (value: string) => void;
}> = ({ name, outerLabel, placeholder, options, disabled, onChangeCallback }) => {
  const { control } = useFormContext<OrgSettingsFormValues>();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const currentValue = typeof field.value === 'string' && field.value ? field.value : null;
        return (
          <Stack gap="0.6rem">
            <FormLabel style={{ fontSize: 12 }}>{outerLabel}</FormLabel>
            <Autocomplete
              options={options}
              value={currentValue}
              disabled={disabled}
              autoHighlight
              onChange={(_, next) => {
                const value = next ?? '';
                field.onChange(value);
                onChangeCallback?.(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  InputProps={{ ...params.InputProps, style: { fontSize: 12 } }}
                />
              )}
            />
          </Stack>
        );
      }}
    />
  );
};

const firstOf = (value: unknown): string => {
  if (Array.isArray(value)) return String(value[0] ?? '');
  if (value == null) return '';
  return String(value);
};

const useStyles = makeStyles(() =>
  createStyles({
    profileCard: {
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    profileAvatar: {
      width: '70px',
      height: '70px',
    },
    profileInfo: {
      flex: 1,
    },
    profileName: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1E293B',
      marginBottom: '4px',
      fontFamily: 'Inter, sans-serif',
    },
    imageGuidelines: {
      fontSize: '14px',
      color: '#64748B',
      fontFamily: 'Inter, sans-serif',
    },
    uploadButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#94A3B8',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'not-allowed',
      fontFamily: 'Inter, sans-serif',
      opacity: 0.7,
      pointerEvents: 'none',
    },
    formContainer: {
      padding: '10px 24px',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#1E293B',
      marginBottom: '24px',
      fontFamily: 'Inter, sans-serif',
    },
    saveActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '24px',
    },
  }),
);

interface OrgSettingsFormValues extends Record<string, unknown> {
  avatar: string;
  organizationName: string;
  organizationTypeId: string;
  email: string;
  contactNo: string;
  website: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  timeZone: string;
  secondaryEmail: string;
  description: string;
}

const defaultValues: OrgSettingsFormValues = {
  avatar: '',
  organizationName: '',
  organizationTypeId: '',
  email: '',
  contactNo: '',
  website: '',
  street: '',
  postalCode: '',
  city: '',
  country: '',
  timeZone: '',
  secondaryEmail: '',
  description: '',
};

const mapOrgToFormValues = (org: Record<string, unknown> | null | undefined): OrgSettingsFormValues => {
  if (!org) return { ...defaultValues };

  const organizationType = org.organizationType as { id?: string; name?: string } | undefined;
  const type = org.type as { id?: string; _id?: string } | string | undefined;

  return {
    avatar: String(org.avatar ?? org.logo ?? ''),
    organizationName: String(org.name ?? ''),
    organizationTypeId: String(
      org.organizationTypeId
      ?? organizationType?.id
      ?? (typeof type === 'object' && type != null ? type.id ?? type._id : type)
      ?? '',
    ),
    email: String(org.email ?? ''),
    contactNo: firstOf(org.contactNo),
    website: String(org.website ?? ''),
    street: String(org.street ?? ''),
    postalCode: String(org.postal ?? org.postalCode ?? ''),
    city: String(org.city ?? ''),
    country: String(org.country ?? ''),
    timeZone: String(org.timeZone ?? ''),
    secondaryEmail: firstOf(org.secondaryEmail),
    description: String(org.description ?? ''),
  };
};

/** Company Settings form — rendered as the Company Settings tab on /user-settings. */
export const OrgSettingsPanel: FC = () => {
  const { t } = useTranslation('orgSettings');
  const classes = useStyles();
  const { user } = useAuth();
  const orgId = user?.organization?.id ?? '';
  const [orgTypeOptions, setOrgTypeOptions] = useState<
    Array<{ id: string; label: string; value: string }>
  >([]);

  const methods = useForm<OrgSettingsFormValues>({
    resolver: yupResolver(
      object({
        organizationName: string().trim().required(t('companyNameRequired')),
        email: string()
          .transform((value) => (value?.trim() === '' ? undefined : value))
          .email(t('invalidEmail')),
        secondaryEmail: string()
          .transform((value) => (value?.trim() === '' ? undefined : value))
          .email(t('invalidEmail')),
      }),
    ) as any,
    defaultValues,
  });

  const {
    formState: { isSubmitting },
    watch,
    setValue,
    reset,
    handleSubmit,
  } = methods;

  const selectedCountry = watch('country');
  const avatar = watch('avatar');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!orgId) {
        toast.error('Organization not found');
        return;
      }

      try {
        const [typesResponse, orgResponse] = await Promise.all([
          organizationApi.getOrganizationTypes(),
          organizationApi.getById(orgId),
        ]);

        const organizationTypes = Array.isArray(typesResponse?.data?.data)
          ? typesResponse.data.data
          : Array.isArray(typesResponse?.data)
            ? typesResponse.data
            : [];

        const mappedOptions = organizationTypes
          .map((item: Record<string, unknown>, index: number) => {
            const value = String(item?.id || item?._id || item?.name || `org-type-${index}`);
            const label = String(item?.name || item?.label || value);
            return { id: value, value, label };
          })
          .filter((option) => option.label.trim().length > 0);

        const org = (orgResponse?.data?.data ?? orgResponse?.data) as
          | Record<string, unknown>
          | undefined;
        const formValues = mapOrgToFormValues(org);
        const typeName = String(
          (org?.organizationType as { name?: string } | undefined)?.name
          ?? org?.organizationTypeName
          ?? '',
        );

        if (
          formValues.organizationTypeId
          && typeName
          && !mappedOptions.some((option) => option.value === formValues.organizationTypeId)
        ) {
          mappedOptions.unshift({
            id: formValues.organizationTypeId,
            value: formValues.organizationTypeId,
            label: typeName,
          });
        }

        if (!cancelled) {
          setOrgTypeOptions(mappedOptions);
          reset(formValues);
        }
      } catch (error: any) {
        if (!cancelled) {
          setOrgTypeOptions([]);
          toast.error(error?.response?.data?.message ?? 'Failed to load organization');
          reset({ ...defaultValues });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [orgId, reset]);

  const onSubmit = async (values: OrgSettingsFormValues) => {
    if (!orgId) {
      toast.error('Organization not found');
      return;
    }

    const contactNo = values.contactNo?.trim() ? [values.contactNo.trim()] : [];
    const secondaryEmail = values.secondaryEmail?.trim()
      ? [values.secondaryEmail.trim()]
      : [];

    // Do not send securityComplinaceFrameworks here — Compliance Settings owns
    // frameworks via dedicated /organization/.../frameworks APIs. Including []
    // from a getById payload that omits the field would wipe saved frameworks.
    const payload = {
      name: values.organizationName?.trim() ?? '',
      email: values.email?.trim() ?? '',
      type: values.organizationTypeId || undefined,
      contactNo,
      website: values.website?.trim() ?? '',
      street: values.street?.trim() ?? '',
      postal: values.postalCode?.trim() ?? '',
      city: values.city?.trim() ?? '',
      country: values.country?.trim() ?? '',
      timeZone: values.timeZone ?? '',
      description: values.description?.trim() ?? '',
      secondaryEmail,
    };

    try {
      await organizationApi.updateById(orgId, payload);
      toast.success('Updated Successfully');
      const response = await organizationApi.getById(orgId);
      const org = (response?.data?.data ?? response?.data) as Record<string, unknown> | undefined;
      reset(mapOrgToFormValues(org));
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? 'Failed to save organization');
    }
  };

  return (
    <RHFProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.profileCard}>
        <Avatar className={classes.profileAvatar} src={avatar || dummyAvatar} />
        <div className={classes.profileInfo}>
          <div className={classes.profileName}>{t('uploadImage')}</div>
          <div className={classes.imageGuidelines}>{t('imageHint')}</div>
        </div>
        <span className={classes.uploadButton}>
          <Upload fontSize="small" />
          {t('upload')}
        </span>
      </div>

      <div className={classes.formContainer}>
        <div className={classes.sectionTitle}>{t('organizationInformation')}</div>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFTextField
              name="organizationName"
              outerLabel={t('companyName')}
              placeholder={t('companyName')}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFCustomSelect
              name="organizationTypeId"
              outerLabel={t('organizationType')}
              options={orgTypeOptions}
              placeholder={t('selectType')}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFTextField
              name="email"
              outerLabel={t('email')}
              placeholder={t('email')}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFTextField
              name="contactNo"
              outerLabel={t('contactNumber')}
              placeholder={t('contactNumber')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFTextField
              name="website"
              outerLabel={t('website')}
              placeholder={t('website')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFTextField
              name="street"
              outerLabel={t('street')}
              placeholder={t('street')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFTextField
              name="postalCode"
              outerLabel={t('postalCode')}
              placeholder={t('postalCode')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFCustomSelect
              name="country"
              outerLabel={t('country')}
              options={countryOptions}
              placeholder={t('country')}
              onChangeCallback={() => setValue('city', '')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <StringAutocomplete
              name="city"
              outerLabel={t('city')}
              placeholder={t('city')}
              options={getCitiesByCountry(selectedCountry)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <StringAutocomplete
              name="timeZone"
              outerLabel={t('timeZone')}
              placeholder={t('timeZone')}
              options={timeZones}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RHFTextField
              name="secondaryEmail"
              outerLabel={t('secondaryEmail')}
              placeholder={t('secondaryEmail')}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RHFTextField
              name="description"
              outerLabel={t('description')}
              placeholder={t('descriptionPlaceholder')}
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
        <Box className={classes.saveActions}>
          <Button type="submit" loading={isSubmitting} disabled={!orgId || isSubmitting}>
            {t('saveChanges')}
          </Button>
        </Box>
      </div>
    </RHFProvider>
  );
};
